import React, { createContext, useContext, useState } from "react";
import { View, Text, Pressable, StyleSheet } from "react-native";
import { colors, spacing, radii, typographyScale, typographyStyles, shadowStyles } from "../../../styles/design-system";

type TabsVariant = "default" | "soft";

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tabs components must be used within a Tabs provider");
  }
  return context;
}

// Tabs Root
interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}

export function Tabs({ value, onValueChange, children }: TabsProps) {
  return (
    <TabsContext.Provider value={{ value, onValueChange }}>
      <View>{children}</View>
    </TabsContext.Provider>
  );
}

// TabsList
interface TabsListProps {
  children: React.ReactNode;
  variant?: TabsVariant;
  style?: object;
}

export function TabsList({ children, variant = "default", style }: TabsListProps) {
  return (
    <View style={[styles.tabsList, variant === "default" ? styles.tabsListDefault : styles.tabsListSoft, style]}>
      {children}
    </View>
  );
}

// TabsTrigger
interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  variant?: TabsVariant;
  style?: object;
}

export function TabsTrigger({ value, children, variant = "default", style }: TabsTriggerProps) {
  const { value: selectedValue, onValueChange } = useTabsContext();
  const isActive = selectedValue === value;
  const [isHovered, setIsHovered] = useState(false);

  // Determine icon color based on state
  const getIconColor = () => {
    if (isActive) {
      return variant === "default" ? colors.grey[900] : colors.brand[700];
    }
    if (isHovered && variant === "soft") {
      return colors.grey[900];
    }
    return colors.grey[600];
  };

  return (
    <Pressable
      onPress={() => onValueChange(value)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={[
        styles.tabsTrigger,
        variant === "default" ? styles.tabsTriggerDefault : styles.tabsTriggerSoft,
        isActive && (variant === "default" ? styles.tabsTriggerActiveDefault : styles.tabsTriggerActiveSoft),
        !isActive && isHovered && variant === "soft" && styles.tabsTriggerHoverSoft,
        style,
      ]}>
      {typeof children === "string" ? (
        <Text
          style={[
            styles.tabsTriggerText,
            isActive &&
              (variant === "default" ? styles.tabsTriggerTextActiveDefault : styles.tabsTriggerTextActiveSoft),
            !isActive && isHovered && variant === "soft" && styles.tabsTriggerTextHoverSoft,
          ]}>
          {children}
        </Text>
      ) : (
        <View style={styles.tabsTriggerContent}>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && typeof child.type !== "string") {
              // Clone icon elements with appropriate color
              return React.cloneElement(child as React.ReactElement<{ color?: string; size?: number }>, {
                color: getIconColor(),
                size: 16,
              });
            }
            if (typeof child === "string") {
              return (
                <Text
                  style={[
                    styles.tabsTriggerText,
                    isActive &&
                      (variant === "default" ? styles.tabsTriggerTextActiveDefault : styles.tabsTriggerTextActiveSoft),
                    !isActive && isHovered && variant === "soft" && styles.tabsTriggerTextHoverSoft,
                  ]}>
                  {child}
                </Text>
              );
            }
            return child;
          })}
        </View>
      )}
    </Pressable>
  );
}

// TabsContent
interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  style?: object;
}

export function TabsContent({ value, children, style }: TabsContentProps) {
  const { value: selectedValue } = useTabsContext();

  if (selectedValue !== value) {
    return null;
  }

  return <View style={[styles.tabsContent, style]}>{children}</View>;
}

const styles = StyleSheet.create({
  tabsList: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: spacing.xxs,
  },
  tabsListDefault: {
    height: spacing["2xl"],
    borderRadius: radii.sm,
    backgroundColor: colors.grey[100],
  },
  tabsListSoft: {
    gap: spacing.xxs,
    borderRadius: radii.lg,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    alignSelf: "flex-start",
  },
  tabsTrigger: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  tabsTriggerDefault: {
    borderRadius: radii.xs,
    paddingHorizontal: 12,
    paddingVertical: radii.sm,
  },
  tabsTriggerSoft: {
    borderRadius: 12,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  tabsTriggerActiveDefault: {
    backgroundColor: colors.white,
    ...shadowStyles.xs,
  },
  tabsTriggerActiveSoft: {
    backgroundColor: colors.brand[100],
  },
  tabsTriggerHoverSoft: {
    backgroundColor: colors.grey[100],
  },
  tabsTriggerContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  tabsTriggerText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[600],
  },
  tabsTriggerTextActiveDefault: {
    color: colors.grey[900],
  },
  tabsTriggerTextActiveSoft: {
    color: colors.brand[700],
  },
  tabsTriggerTextHoverSoft: {
    color: colors.grey[900],
  },
  tabsContent: {
    marginTop: spacing.xs,
  },
});

export type { TabsVariant };
